import React from 'react';

import { ActionIcon, Box, Button, Text, createStyles, useMantineTheme } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import useUpdateAthleteClass from 'hooks/athleteClass/useUpdateAthleteClass';
import { useNavigate } from 'react-router-dom';
import { AthleteKiloClassWithAthlete } from 'types/generated/models/AthleteKiloClassWithAthlete';
import notificationHandler from 'utils/notifications';

const useStyles = createStyles(theme => ({
  row: {
    display: 'grid',
    alignItems: 'center',
    padding: theme.spacing.xs,
    gridTemplateColumns: 'min-content auto min-content min-content',
    gridTemplateRows: 'auto',
    gap: theme.spacing.sm,
    borderTop: `1px solid ${theme.colors.gray[3]}`,
  },
}));

interface IWaitlistedAthletesProps {
  athleteKiloClass: AthleteKiloClassWithAthlete;
  index: number;
  handleDeleteModal: (id: string) => void;
  permission?: boolean;
}

const WaitlistedAthletes = ({
  athleteKiloClass,
  index,
  handleDeleteModal,
  permission = true,
}: IWaitlistedAthletesProps): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const navigate = useNavigate();

  const updateAthleteKiloClassMutation = useUpdateAthleteClass();

  const handleUpdate = (akc: AthleteKiloClassWithAthlete): void => {
    const { successNotification, errorNotification } = notificationHandler();
    const payload = {
      ...akc,
      isReserved: true,
      isWaitlisted: false,
      athlete: akc.athlete.athleteId,
      isLateCancel: false,
      isNoShow: false,
      chargeNow: false,
      chargeDate: null,
      chargeFee: null,
      taxRate: null,
      deductCredit: null,
      isKioskSelfService: false,
    };
    updateAthleteKiloClassMutation.mutate(payload, {
      onSuccess: () => {
        successNotification(
          'Athlete reservation update success',
          `${akc.athlete.fullName}'s reservation successfully updated.`,
        );
      },
      onError: error => {
        errorNotification('Athlete reservation update failed', error.response?.data.message);
      },
    });
  };

  const handleNameClick = (): void => {
    navigate(`/users/athletes/${athleteKiloClass.athlete.athleteId}/general-info`);
  };

  return (
    <Box className={classes.row}>
      <Text c='dimmed' size='sm'>
        {index + 1}
      </Text>
      <Text
        fw='bold'
        size='sm'
        underline
        sx={{ cursor: 'pointer', width: 'fit-content' }}
        onClick={() => handleNameClick()}
      >
        {athleteKiloClass.athlete.fullName}
      </Text>
      <Button
        size='sm'
        compact
        onClick={() => handleUpdate(athleteKiloClass)}
        loading={updateAthleteKiloClassMutation.isLoading}
        disabled={!permission}
      >
        Move to Reserved
      </Button>
      <ActionIcon
        onClick={() => handleDeleteModal(athleteKiloClass.athleteKiloClassId)}
        variant='transparent'
        color='gray.7'
        disabled={!permission}
      >
        <IconTrash size={theme.fontSizes.xl} />
      </ActionIcon>
    </Box>
  );
};

export default WaitlistedAthletes;
